import {
  ExternalRouteRef,
  RouteRef,
  SubRouteRef,
  createExternalRouteRef,
  createRouteRef,
  createSubRouteRef,
} from '@backstage/core-plugin-api';

import {
  workflowRouteRef as workflowExternalRouteRef,
  workflowInstanceRouteRef as workflowInstanceExternalRouteRef,
  workflowsRouteRef as workflowsExternalRouteRef,
} from '@spotify/plugin-confidence-workflows-react';

export enum RoutesIds {
  workflows = 'workflows',
  workflow = 'workflow',
  workflowInstance = 'workflowInstance',
}

enum ExternalRoutesIds {
  metric = 'metric',
  metrics = 'metrics',
  entities = 'entities',
  entity = 'entity',
  assignmentTable = 'assignmentTable',
  assignmentTablesCreate = 'assignmentTablesCreate',
  factTable = 'factTable',
  flag = 'flag',
  flags = 'flags',
  surface = 'surface',
  surfaceAvailability = 'surfaceAvailability',
  experiments = 'experiments',
  labels = 'labels',
}

// root route
export const rootRouteRef = createRouteRef({ id: RoutesIds.workflows });

// sub-routes within the plugin
export const workflowRouteRef = createSubRouteRef({
  id: RoutesIds.workflow,
  path: '/:workflow',
  parent: rootRouteRef,
});

export const workflowInstanceLegacyRouteRef = createSubRouteRef({
  id: RoutesIds.workflowInstance,
  path: '/:workflow/:id',
  parent: rootRouteRef,
});

export const workflowInstanceRouteRef = createSubRouteRef({
  id: RoutesIds.workflowInstance,
  path: '/:workflow/instances/:id',
  parent: rootRouteRef,
});

// external routes to other plugins
export const metricRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.metric,
  params: ['id'],
  optional: true,
});

export const metricsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.metrics,
  optional: true,
});

export const entitiesRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.entities,
  optional: true,
});

export const entityRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.entity,
  params: ['id'],
  optional: true,
});

export const assignmentTableRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.assignmentTable,
  params: ['id'],
  optional: true,
});

export const assignmentTablesCreateRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.assignmentTablesCreate,
  optional: true,
});

export const factTableRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.factTable,
  params: ['id'],
  optional: true,
});

export const flagRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.flag,
  params: ['id'],
  optional: true,
});

export const flagsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.flags,
  optional: true,
});

export const surfaceRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.surface,
  params: ['id'],
  optional: true,
});

export const surfaceAvailabilityRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.surfaceAvailability,
  params: ['id'],
  optional: true,
});

export const experimentsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.experiments,
  optional: true,
});

export const labelsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.labels,
  optional: true,
});

// default routes/routes to be used by other plugins
export const Routes = {
  workflows: rootRouteRef,
  workflow: workflowRouteRef,
  workflowInstance: workflowInstanceRouteRef,
} satisfies Record<RoutesIds, SubRouteRef | RouteRef>;

export const ExternalRoutes = {
  // sub-routes within the plugin
  workflows: workflowsExternalRouteRef,
  workflow: workflowExternalRouteRef,
  workflowInstance: workflowInstanceExternalRouteRef,

  // external routes to other plugins
  metric: metricRouteRef,
  metrics: metricsRouteRef,
  entities: entitiesRouteRef,
  entity: entityRouteRef,
  factTable: factTableRouteRef,
  flag: flagRouteRef,
  flags: flagsRouteRef,
  assignmentTable: assignmentTableRouteRef,
  assignmentTablesCreate: assignmentTablesCreateRouteRef,
  surface: surfaceRouteRef,
  surfaceAvailability: surfaceAvailabilityRouteRef,
  experiments: experimentsRouteRef,
  labels: labelsRouteRef,
} satisfies Record<RoutesIds, ExternalRouteRef> &
  Record<ExternalRoutesIds, ExternalRouteRef>;
